import { useMemo, useState, useEffect } from 'react';

const useIsDarkMode = () => {
  const [isDark, setIsDark] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const el = document.documentElement;
    const update = () => setIsDark(el.classList.contains('dark'));
    update();
    const observer = new MutationObserver(update);
    observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return isDark;
};

/**
 * Hook personnalisé pour générer les styles de React Select
 * avec prise en charge du mode sombre
 * @param {Object} customStyles - Styles personnalisés optionnels pour écraser les valeurs par défaut
 * @returns {Object} Styles configurés pour React Select
 */
export const useSelectStyles = (customStyles = {}) => {
  const isDarkMode = useIsDarkMode();
  return useMemo(() => {
    // Styles de base avec support du mode sombre
    const baseStyles = {
      control: (provided, state) => ({
        ...provided,
        backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
        borderColor: isDarkMode ? '#404040' : 'rgb(229 231 235)',
        borderRadius: '0.5rem',
        boxShadow: state.isFocused
          ? isDarkMode
            ? '0 0 0 3px rgba(59, 130, 246, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.05)'
            : '0 0 0 3px rgba(59, 130, 246, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.05)'
          : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        '&:hover': {
          borderColor: isDarkMode ? '#505050' : 'rgb(209 213 219)',
        },
        minHeight: '40px',
        height: 'auto',
        transition: 'all 0.2s ease'
      }),
      valueContainer: (provided) => ({
        ...provided,
        padding: '4px 12px',
        height: 'auto',
        maxHeight: '80px',
        overflowY: 'auto',
        gap: '4px'
      }),
      menu: (provided) => ({
        ...provided,
        backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
        borderRadius: '0.5rem',
        border: isDarkMode ? '1px solid #404040' : '1px solid rgb(229 231 235)',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        marginTop: '4px',
        zIndex: 9999,
        overflow: 'hidden'
      }),
      menuList: (provided) => ({
        ...provided,
        padding: '4px'
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: isDarkMode
          ? state.isSelected
            ? 'rgb(59 130 246)'
            : state.isFocused
              ? '#3a3a3a'
              : 'transparent'
          : state.isSelected
            ? 'rgb(59 130 246)'
            : state.isFocused
              ? 'rgb(243 244 246)'
              : 'transparent',
        color: state.isSelected
          ? 'white'
          : isDarkMode ? 'rgb(229 231 235)' : 'rgb(17 24 39)',
        borderRadius: '0.375rem',
        padding: '8px 12px',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        fontSize: '0.875rem',
        ':active': {
          backgroundColor: isDarkMode
            ? state.isSelected ? 'rgb(59 130 246)' : '#4a4a4a'
            : state.isSelected ? 'rgb(59 130 246)' : 'rgb(229 231 235)'
        }
      }),
      singleValue: (provided) => ({
        ...provided,
        color: isDarkMode ? 'rgb(229 231 235)' : 'rgb(17 24 39)',
        fontSize: '0.875rem'
      }),
      multiValue: (provided) => ({
        ...provided,
        backgroundColor: isDarkMode ? '#3a3a3a' : 'rgb(243 244 246)',
        borderRadius: '0.375rem',
        padding: '2px'
      }),
      multiValueLabel: (provided) => ({
        ...provided,
        color: isDarkMode ? 'rgb(229 231 235)' : 'rgb(17 24 39)',
        fontSize: '0.75rem',
        padding: '2px 6px'
      }),
      multiValueRemove: (provided) => ({
        ...provided,
        color: isDarkMode ? 'rgb(156 163 175)' : 'rgb(107 114 128)',
        borderRadius: '0 0.25rem 0.25rem 0',
        ':hover': {
          backgroundColor: isDarkMode ? '#4a4a4a' : 'rgb(229 231 235)',
          color: isDarkMode ? 'rgb(239 68 68)' : 'rgb(220 38 38)'
        }
      }),
      input: (provided) => ({
        ...provided,
        color: isDarkMode ? 'rgb(229 231 235)' : 'rgb(17 24 39)',
        margin: 0,
        padding: 0,
        fontSize: '0.875rem'
      }),
      placeholder: (provided) => ({
        ...provided,
        color: isDarkMode ? 'rgb(156 163 175)' : 'rgb(156 163 175)',
        fontSize: '0.875rem'
      }),
      indicatorSeparator: (provided) => ({
        ...provided,
        backgroundColor: isDarkMode ? '#404040' : 'rgb(229 231 235)',
        margin: '8px 0'
      }),
      dropdownIndicator: (provided, state) => ({
        ...provided,
        color: isDarkMode ? 'rgb(156 163 175)' : 'rgb(107 114 128)',
        padding: '0 8px',
        transition: 'all 0.2s ease',
        transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        '&:hover': {
          color: isDarkMode ? 'rgb(209 213 219)' : 'rgb(75 85 99)'
        }
      }),
      clearIndicator: (provided) => ({
        ...provided,
        color: isDarkMode ? 'rgb(156 163 175)' : 'rgb(107 114 128)',
        padding: '0 8px',
        ':hover': {
          color: isDarkMode ? 'rgb(239 68 68)' : 'rgb(220 38 38)'
        }
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 9999
      })
    };

    // Fusionner avec les styles personnalisés fournis en paramètre
    const mergedStyles = { ...baseStyles };

    // Pour chaque clé dans customStyles, vérifier si elle existe déjà et la merger si nécessaire
    Object.keys(customStyles).forEach(key => {
      if (typeof customStyles[key] === 'function' && typeof baseStyles[key] === 'function') {
        // Cas où les deux sont des fonctions, wrapper la fonction de base
        mergedStyles[key] = (...args) => {
          const baseResult = baseStyles[key](...args);
          const customResult = customStyles[key](...args);
          return { ...baseResult, ...customResult };
        };
      } else {
        // Cas simple où on remplace complètement
        mergedStyles[key] = customStyles[key];
      }
    });

    return mergedStyles;
  }, [customStyles, isDarkMode]);
};

/**
 * Version non-hook des styles React Select (pour les cas où les hooks ne peuvent pas être utilisés)
 * @param {boolean} isDarkMode - Indique si le mode sombre est actif
 * @param {Object} customStyles - Styles personnalisés optionnels
 * @returns {Object} Styles configurés pour React Select
 */
export const getSelectStyles = (isDarkMode = false, customStyles = {}) => {
  // Même logique que dans le hook, mais sans useMemo
  // ...code similaire mais sans hook...

  const baseStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#374151' : provided.backgroundColor,
      // Le reste est identique à la version hook
    }),
    // ...autres styles identiques à la version hook
  };

  // Fusion des styles personnalisés (même logique que dans le hook)
  // ...

  return baseStyles;
};